import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import DataService from "../services/data.service";
import ErrorBoundary from "./ErrorBoundary";
import { formatCurrency } from "../common/reporting";

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [showFloatModal, setShowFloatModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [userId, setUserId] = useState("");
  const [modalUserIds, setModalUserIds] = useState([]);
  const [successfulFloatMessage, setSuccessfulFloatMessage] = useState("");
  const [failedFloatMessage, setFailedFloatMessage] = useState("");
  const [linkMessage, setLinkMessage] = useState("");

  const loadData = () => {
    Promise.all([DataService.getAllClients(), DataService.getUsers()]).then(
      ([clientsResponse, usersResponse]) => {
        setClients(clientsResponse.data);
        setUsers(usersResponse.data);
      }
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const linkedUsers = useMemo(() => {
    return modalUserIds
      .map((modalUserId) => users.find((user) => user.users.id === modalUserId))
      .filter(Boolean);
  }, [modalUserIds, users]);

  const metrics = useMemo(() => {
    return {
      totalClients: clients.length,
      totalBalance: clients.reduce(
        (runningTotal, client) => runningTotal + Number(client.balance || 0),
        0
      ),
      linkedAccounts: clients.filter((client) => client.userIds && client.userIds.length).length,
    };
  }, [clients]);

  const openFloatModal = (client) => {
    setShowFloatModal(true);
    setAccountNumber(client.accountNumber);
    setCustomerName(client.clientName);
    setPhoneNumber(client.phoneNumber);
    setAmount("");
    setDescription("");
    setSuccessfulFloatMessage("");
    setFailedFloatMessage("");
  };

  const closeFloatModal = () => {
    setShowFloatModal(false);
    setAccountNumber("");
    setCustomerName("");
    setPhoneNumber("");
    setAmount("");
    setDescription("");
    setSuccessfulFloatMessage("");
    setFailedFloatMessage("");
  };

  const openLinkModal = (client) => {
    setShowLinkModal(true);
    setAccountNumber(client.accountNumber);
    setCustomerName(client.clientName);
    setPhoneNumber(client.phoneNumber);
    setModalUserIds(client.userIds || []);
    setUserId("");
    setLinkMessage("");
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setAccountNumber("");
    setCustomerName("");
    setPhoneNumber("");
    setModalUserIds([]);
    setUserId("");
    setLinkMessage("");
  };

  const handleSubmitUpdateFloat = (event) => {
    event.preventDefault();

    if (!amount) {
      setFailedFloatMessage("Amount is required.");
      setSuccessfulFloatMessage("");
      return;
    }

    if (!description) {
      setFailedFloatMessage("Float description is required.");
      setSuccessfulFloatMessage("");
      return;
    }

    DataService.updateFloat(
      accountNumber,
      parseFloat(amount),
      phoneNumber,
      customerName,
      description
    ).then(
      () => {
        setSuccessfulFloatMessage("Client float updated successfully.");
        setFailedFloatMessage("");
        loadData();
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setFailedFloatMessage(responseMessage);
      }
    );
  };

  const handleSubmitLink = (event) => {
    event.preventDefault();

    if (!userId) {
      setLinkMessage("Select a portal user before linking this account.");
      return;
    }

    DataService.linkUser(accountNumber, userId).then(() => {
      setLinkMessage("User linked successfully.");
      loadData();
      setModalUserIds((currentValue) => [...currentValue, userId]);
      setUserId("");
    });
  };

  const handleDelink = (selectedUserId) => {
    DataService.delinkUser(accountNumber, selectedUserId).then(() => {
      setModalUserIds((currentValue) =>
        currentValue.filter((modalUserId) => modalUserId !== selectedUserId)
      );
      loadData();
    });
  };

  return (
    <div className="portal-page-stack">
      <section className="metrics-grid">
        <article className="metric-card">
          <p className="metric-card__label">Managed clients</p>
          <h4>{metrics.totalClients}</h4>
          <span>Client accounts currently under administration in the portal.</span>
        </article>
        <article className="metric-card metric-card--highlight">
          <p className="metric-card__label">Combined float</p>
          <h4>{formatCurrency(metrics.totalBalance)}</h4>
          <span>Total liquidity visible across the client portfolio.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Linked accounts</p>
          <h4>{metrics.linkedAccounts}</h4>
          <span>Client profiles already mapped to at least one portal user.</span>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Client administration</p>
            <h4>Corporate client accounts</h4>
          </div>
          <div className="button-row">
            <Button variant="outline-primary" onClick={loadData}>
              Refresh
            </Button>
            <Link to="/createclient">
              <Button variant="primary">Add client</Button>
            </Link>
          </div>
        </div>
        {clients.length ? (
          <div className="table-responsive portal-table-wrap">
            <table className="table portal-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Client name</th>
                  <th>Account ID</th>
                  <th>Phone number</th>
                  <th>Balance</th>
                  <th>Linked users</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.accountNumber}>
                    <td>{client.clientName}</td>
                    <td>{client.accountNumber}</td>
                    <td>{client.phoneNumber}</td>
                    <td>{formatCurrency(client.balance)}</td>
                    <td>{client.userIds ? client.userIds.length : 0}</td>
                    <td>
                      <div className="table-action-row">
                        <Button variant="outline-primary" size="sm" onClick={() => openFloatModal(client)}>
                          Update float
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => openLinkModal(client)}>
                          Manage links
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state empty-state--compact">
            <span className="bi bi-buildings" aria-hidden="true" />
            <h5>No clients available</h5>
            <p>Create a client account to begin managing balances and user links.</p>
          </div>
        )}
      </section>

      <Modal show={showFloatModal} onHide={closeFloatModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update client float</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-supporting-text">
            Record a float adjustment for this client account and include a clear audit description.
          </p>
          <Form onSubmit={handleSubmitUpdateFloat}>
            <Form.Group className="mb-3" controlId="updateFloatAccountNumber">
              <Form.Label>Account number</Form.Label>
              <Form.Control type="text" disabled value={accountNumber} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="updateFloatClientName">
              <Form.Label>Client name</Form.Label>
              <Form.Control type="text" disabled value={customerName} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="updateFloatAmount">
              <Form.Label>Amount</Form.Label>
              <Form.Control type="number" value={amount} onChange={(event) => setAmount(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-0" controlId="updateFloatDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Form.Group>
          </Form>
          {failedFloatMessage ? <Alert variant="danger" className="mt-3 mb-0">{failedFloatMessage}</Alert> : null}
          {successfulFloatMessage ? <Alert variant="success" className="mt-3 mb-0">{successfulFloatMessage}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeFloatModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitUpdateFloat}>
            Save adjustment
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLinkModal} onHide={closeLinkModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage linked users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-supporting-text">
            Review which users have access to <strong>{customerName}</strong> and link additional operators when needed.
          </p>
          {linkedUsers.length ? (
            <div className="table-responsive portal-table-wrap mb-3">
              <table className="table portal-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {linkedUsers.map((linkedUser) => (
                    <tr key={linkedUser.users.id}>
                      <td>{linkedUser.users.userName}</td>
                      <td>{linkedUser.users.email}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelink(linkedUser.users.id)}
                        >
                          Delink
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state empty-state--compact mb-3">
              <span className="bi bi-person-plus" aria-hidden="true" />
              <h5>No linked users</h5>
              <p>Link the first operator to give this client portal access.</p>
            </div>
          )}

          <Form onSubmit={handleSubmitLink}>
            <Form.Group className="mb-0" controlId="linkPortalUser">
              <Form.Label>Select user</Form.Label>
              <Form.Control as="select" value={userId} onChange={(event) => setUserId(event.target.value)}>
                <option value="">Choose a user</option>
                {users.map((user) => (
                  <option value={user.users.id} key={user.users.id}>
                    {user.users.userName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
          {linkMessage ? <Alert variant="info" className="mt-3 mb-0">{linkMessage}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeLinkModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmitLink}>
            Link user
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const Wrapper = () => {
  return (
    <ErrorBoundary>
      <Clients />
    </ErrorBoundary>
  );
};

export default Wrapper;
